// Small visx chart kit: lines (S-curves), grouped bars (histograms), horizontal bars, donut. Tooltip, legend, PNG export.
import { ParentSize } from "@visx/responsive";
import { scaleBand, scaleLinear } from "@visx/scale";
import { LinePath, Bar, Pie, Line } from "@visx/shape";
import { AxisBottom, AxisLeft } from "@visx/axis";
import { GridRows } from "@visx/grid";
import { Group } from "@visx/group";
import { localPoint } from "@visx/event";
import { curveLinear } from "@visx/curve";
import { useRef, useState, type ReactNode } from "react";
import { Download } from "lucide-react";
import { useNeutral, usePrinting } from "@/lib/theme";

export const COLORS = { planned: "#2f6db5", earned: "#16a34a", forecast: "#f59e0b", actual: "#dc2626", indirect: "#7c3aed", indirectPlanned: "#c084fc", bac: "#64748b" };

export interface SeriesDef {
  key: string;
  label: string;
  color: string;
  values: (number | null)[];
  dashed?: boolean;
}

const M = { top: 12, right: 16, bottom: 44, left: 56 };
/**
 * Width a chart is drawn at when printing (about a Letter/A4 page less margins). The browser lays out the printout
 * after the charts have rendered, so the SVG then stretches to the page width (print:w-full) instead of keeping
 * whatever width the screen gave it.
 */
const PRINT_WIDTH = 700;

/**
 * Measures the chart's box on screen. When printing it draws at PRINT_WIDTH outside ParentSize, whose clipped
 * measuring box would hide an SVG stretched to the page.
 */
function Sized({ printHeight, children }: { printHeight: number; children: (size: { width: number; height: number }) => ReactNode }) {
  const printing = usePrinting();
  return printing ? <>{children({ width: PRINT_WIDTH, height: printHeight })}</> : <ParentSize>{children}</ParentSize>;
}

function Frame({ title, children, onExport, legend }: { title?: ReactNode; children: ReactNode; onExport?: () => void; legend?: ReactNode }) {
  return (
    <div className="flex h-full flex-col print:h-auto">
      {(title || onExport) && (
        <div className="mb-1 flex items-center justify-between">
          <div className="text-sm font-semibold text-slate-700">{title}</div>
          {onExport && (
            <button className="no-print rounded p-1 text-slate-500 hover:bg-slate-100" title="Download PNG" aria-label="Download chart" onClick={onExport}>
              <Download className="size-4" />
            </button>
          )}
        </div>
      )}
      <div className="min-h-0 flex-1">{children}</div>
      {legend}
    </div>
  );
}

export function exportSvgPng(svg: SVGSVGElement | null, name: string, background = "white") {
  if (!svg) return;
  const xml = new XMLSerializer().serializeToString(svg);
  const img = new Image();
  const { width, height } = svg.getBoundingClientRect();
  img.onload = () => {
    const c = document.createElement("canvas");
    c.width = width * 2;
    c.height = height * 2;
    const g = c.getContext("2d")!;
    g.fillStyle = background;
    g.fillRect(0, 0, c.width, c.height);
    g.scale(2, 2);
    g.drawImage(img, 0, 0);
    const a = document.createElement("a");
    a.download = `${name}.png`;
    a.href = c.toDataURL("image/png");
    a.click();
  };
  img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(xml);
}

// Swatches are SVG strokes, not CSS backgrounds, so they still print when the browser skips background graphics.
function Legend({ series }: { series: { label: string; color: string; dashed?: boolean }[] }) {
  return (
    <div className="mt-1 flex flex-wrap gap-x-3.5 gap-y-1 text-xs">
      {series.map((s) => (
        <span key={s.label} className="inline-flex items-center gap-1">
          <svg width={14} height={4} aria-hidden>
            <line x1={0} y1={2} x2={14} y2={2} stroke={s.color} strokeWidth={3} strokeDasharray={s.dashed ? "4 2" : undefined} />
          </svg>
          {s.label}
        </span>
      ))}
    </div>
  );
}

function pickTicks(n: number, max = 10) {
  const step = Math.max(1, Math.ceil(n / max));
  return (i: number) => i % step === 0 || i === n - 1;
}

export function LineChart({
  labels,
  series,
  height = 320,
  yFormat = (v) => String(v),
  title,
  name = "chart",
  marker,
  yMax,
}: {
  labels: string[];
  series: SeriesDef[];
  height?: number;
  yFormat?: (v: number) => string;
  title?: ReactNode;
  name?: string;
  /** Index to draw a vertical "current cutoff" marker at. */
  marker?: number;
  yMax?: number;
}) {
  const ref = useRef<SVGSVGElement>(null);
  const N = useNeutral();
  const [hover, setHover] = useState<number | null>(null);
  return (
    <div style={{ height }} className="print:h-auto! print:break-inside-avoid">
      <Frame title={title} onExport={() => exportSvgPng(ref.current, name, N.surface)} legend={<Legend series={series} />}>
        <Sized printHeight={height - 60}>
          {({ width, height: h }) => {
            if (width < 50 || h < 50) return null;
            const iw = width - M.left - M.right;
            const ih = h - M.top - M.bottom;
            const all = series.flatMap((s) => s.values.filter((v): v is number => v !== null && isFinite(v)));
            const top = yMax ?? (all.length ? Math.max(1e-9, ...all) * 1.05 : 1);
            const x = scaleLinear({ domain: [0, Math.max(1, labels.length - 1)], range: [0, iw] });
            const y = scaleLinear({ domain: [Math.min(0, ...all), top], range: [ih, 0], nice: true });
            const show = pickTicks(labels.length);
            return (
              <svg ref={ref} width={width} height={h} viewBox={`0 0 ${width} ${h}`} className="max-w-full print:h-auto print:w-full" role="img" aria-label={typeof title === "string" ? title : name}>
                <Group left={M.left} top={M.top}>
                  <GridRows scale={y} width={iw} stroke={N.grid} numTicks={5} />
                  {marker !== undefined && marker >= 0 && <Line from={{ x: x(marker), y: 0 }} to={{ x: x(marker), y: ih }} stroke={N.marker} strokeDasharray="4 3" />}
                  {series.map((s) => (
                    <LinePath<[number, number]>
                      key={s.key}
                      data={s.values.map((v, i) => [i, v] as [number, number | null]).filter((p): p is [number, number] => p[1] !== null && isFinite(p[1]))}
                      x={(d) => x(d[0])}
                      y={(d) => y(d[1])}
                      stroke={s.color}
                      strokeWidth={2}
                      strokeDasharray={s.dashed ? "5 4" : undefined}
                      curve={curveLinear}
                    />
                  ))}
                  {hover !== null && (
                    <>
                      <Line from={{ x: x(hover), y: 0 }} to={{ x: x(hover), y: ih }} stroke={N.label} strokeWidth={1} />
                      {series.map((s) => {
                        const v = s.values[hover];
                        return v === null || v === undefined || !isFinite(v) ? null : <circle key={s.key} cx={x(hover)} cy={y(v)} r={3.5} fill={s.color} />;
                      })}
                    </>
                  )}
                  <AxisLeft scale={y} numTicks={5} tickFormat={(v) => yFormat(Number(v))} stroke={N.axis} tickStroke={N.axis} tickLabelProps={{ fontSize: 10, fill: N.tick }} />
                  <AxisBottom
                    top={ih}
                    scale={x}
                    tickValues={labels.map((_, i) => i).filter(show)}
                    tickFormat={(v) => labels[Number(v)] ?? ""}
                    stroke={N.axis}
                    tickStroke={N.axis}
                    tickLabelProps={{ fontSize: 10, fill: N.tick, angle: -30, textAnchor: "end" }}
                  />
                  <rect
                    width={iw}
                    height={ih}
                    fill="transparent"
                    onMouseMove={(e) => {
                      const p = localPoint(e);
                      if (!p) return;
                      const i = Math.round(x.invert(p.x - M.left));
                      setHover(Math.max(0, Math.min(labels.length - 1, i)));
                    }}
                    onMouseLeave={() => setHover(null)}
                  />
                  {hover !== null && (
                    <g transform={`translate(${Math.min(x(hover) + 8, iw - 150)},4)`} pointerEvents="none">
                      <rect width={150} height={18 + series.length * 15} rx={4} fill={N.surface} stroke={N.axis} />
                      <text x={8} y={14} fontSize={11} fontWeight={600} fill={N.text}>
                        {labels[hover]}
                      </text>
                      {series.map((s, i) => (
                        <text key={s.key} x={8} y={29 + i * 15} fontSize={11} fill={s.color}>
                          {s.label}: {s.values[hover] === null || s.values[hover] === undefined ? "–" : yFormat(s.values[hover]!)}
                        </text>
                      ))}
                    </g>
                  )}
                </Group>
              </svg>
            );
          }}
        </Sized>
      </Frame>
    </div>
  );
}

export function BarChart({
  labels,
  series,
  height = 300,
  yFormat = (v) => String(v),
  title,
  name = "bars",
  horizontal,
}: {
  labels: string[];
  series: SeriesDef[];
  height?: number;
  yFormat?: (v: number) => string;
  title?: ReactNode;
  name?: string;
  horizontal?: boolean;
}) {
  const ref = useRef<SVGSVGElement>(null);
  const N = useNeutral();
  const [hover, setHover] = useState<number | null>(null);
  return (
    <div style={{ height }} className="print:h-auto! print:break-inside-avoid">
      <Frame title={title} onExport={() => exportSvgPng(ref.current, name, N.surface)} legend={<Legend series={series} />}>
        <Sized printHeight={height - 60}>
          {({ width, height: h }) => {
            if (width < 50 || h < 50) return null;
            const left = horizontal ? 110 : M.left;
            const iw = width - left - M.right;
            const ih = h - M.top - M.bottom;
            const all = series.flatMap((s) => s.values.filter((v): v is number => v !== null && isFinite(v)));
            const max = Math.max(1e-9, ...all) * 1.05;
            const band = scaleBand({ domain: labels.map((_, i) => i), range: horizontal ? [0, ih] : [0, iw], padding: 0.2 });
            const inner = scaleBand({ domain: series.map((s) => s.key), range: [0, band.bandwidth()], padding: 0.1 });
            const val = scaleLinear({ domain: [0, max], range: horizontal ? [0, iw] : [ih, 0], nice: true });
            const show = pickTicks(labels.length, horizontal ? 40 : 14);
            return (
              <svg ref={ref} width={width} height={h} viewBox={`0 0 ${width} ${h}`} className="max-w-full print:h-auto print:w-full" role="img" aria-label={typeof title === "string" ? title : name}>
                <Group left={left} top={M.top}>
                  {!horizontal && <GridRows scale={val} width={iw} stroke={N.grid} numTicks={5} />}
                  {labels.map((_, i) => (
                    <Group key={i} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
                      {horizontal ? (
                        <rect x={0} y={band(i)} width={iw} height={band.bandwidth()} fill={hover === i ? N.hover : "transparent"} />
                      ) : (
                        <rect x={band(i)} y={0} width={band.bandwidth()} height={ih} fill={hover === i ? N.hover : "transparent"} />
                      )}
                      {series.map((s) => {
                        const v = s.values[i];
                        if (v === null || v === undefined || !isFinite(v) || v <= 0) return null;
                        return horizontal ? (
                          <Bar key={s.key} x={0} y={(band(i) ?? 0) + (inner(s.key) ?? 0)} width={val(v)} height={inner.bandwidth()} fill={s.color} />
                        ) : (
                          <Bar key={s.key} x={(band(i) ?? 0) + (inner(s.key) ?? 0)} y={val(v)} width={inner.bandwidth()} height={ih - val(v)} fill={s.color} />
                        );
                      })}
                    </Group>
                  ))}
                  {horizontal ? (
                    <>
                      <AxisLeft scale={band} tickFormat={(v) => labels[Number(v)] ?? ""} stroke={N.axis} hideTicks tickLabelProps={{ fontSize: 10, fill: N.label }} />
                      <AxisBottom top={ih} scale={val} numTicks={5} tickFormat={(v) => yFormat(Number(v))} stroke={N.axis} tickLabelProps={{ fontSize: 10, fill: N.tick }} />
                    </>
                  ) : (
                    <>
                      <AxisLeft scale={val} numTicks={5} tickFormat={(v) => yFormat(Number(v))} stroke={N.axis} tickLabelProps={{ fontSize: 10, fill: N.tick }} />
                      <AxisBottom
                        top={ih}
                        scale={band}
                        tickValues={labels.map((_, i) => i).filter(show)}
                        tickFormat={(v) => labels[Number(v)] ?? ""}
                        stroke={N.axis}
                        tickLabelProps={{ fontSize: 10, fill: N.tick, angle: -30, textAnchor: "end" }}
                      />
                    </>
                  )}
                  {hover !== null && (
                    <g transform={`translate(${horizontal ? iw - 160 : Math.min((band(hover) ?? 0) + band.bandwidth() + 4, iw - 150)},4)`} pointerEvents="none">
                      <rect width={150} height={18 + series.length * 15} rx={4} fill={N.surface} stroke={N.axis} />
                      <text x={8} y={14} fontSize={11} fontWeight={600} fill={N.text}>
                        {labels[hover]}
                      </text>
                      {series.map((s, k) => (
                        <text key={s.key} x={8} y={29 + k * 15} fontSize={11} fill={s.color}>
                          {s.label}: {s.values[hover] === null || s.values[hover] === undefined ? "–" : yFormat(s.values[hover]!)}
                        </text>
                      ))}
                    </g>
                  )}
                </Group>
              </svg>
            );
          }}
        </Sized>
      </Frame>
    </div>
  );
}

export function Donut({ data, size = 180, title }: { data: { label: string; value: number; color: string; hint?: string }[]; size?: number; title?: ReactNode }) {
  const N = useNeutral();
  const total = data.reduce((s, d) => s + d.value, 0);
  return (
    <div className="print:break-inside-avoid">
      {title && <div className="mb-1 text-sm font-semibold text-slate-700">{title}</div>}
      <div className="flex items-center gap-4">
        <svg width={size} height={size} className="shrink-0" role="img" aria-label="status mix">
          <Group top={size / 2} left={size / 2}>
            {total > 0 ? (
              <Pie data={data.filter((d) => d.value > 0)} pieValue={(d) => d.value} outerRadius={size / 2 - 4} innerRadius={size / 2 - 28} padAngle={0.01}>
                {(pie) => pie.arcs.map((a) => <path key={a.data.label} d={pie.path(a) ?? ""} fill={a.data.color} />)}
              </Pie>
            ) : (
              <circle r={size / 2 - 16} fill="none" stroke={N.grid} strokeWidth={24} />
            )}
            <text textAnchor="middle" dy="0.35em" fontSize={20} fontWeight={600} fill={N.text}>
              {total}
            </text>
          </Group>
        </svg>
        <ul className="space-y-1 text-sm">
          {data.map((d) => (
            <li key={d.label} className="flex items-center gap-2 whitespace-nowrap">
              <svg width={10} height={10} aria-hidden className="shrink-0">
                <rect width={10} height={10} rx={2} fill={d.color} />
              </svg>
              <span className="text-slate-600">{d.label}</span>
              <span className="num font-medium">{d.value}</span>
            </li>
          ))}
        </ul>
      </div>
      {data.some((d) => d.hint) && (
        <div className="mt-2 text-xs text-slate-500">
          {data.filter((d) => d.hint).map((d) => (
            <div key={d.label}>
              {d.label}: {d.hint!.toLowerCase()}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
